<script lang="ts">
import InlineEmojiPicker from '@/components/InlineEmojiPicker.svelte'
import { testIds } from '@/constants'
import { isMessengerFrameOpen, registerEscapeHandler } from '@/modules/app'
import { isSmallInputFontEnabled, isTouchDevice } from '@/modules/device'
import { type EmojiPickerProps, emojiStore } from '@/modules/emojis'
import { checkAndAddFiles } from '@/modules/fileUpload/store'
import { warningMessage } from '@/modules/flashMessages'
import {
	Key,
	messengerInputValue,
	setMessengerInputValue,
	startVisitorTyping,
	stopVisitorTyping,
	TEXTAREA_MAX_LENGTH,
	textareaHeight,
} from '@/modules/textarea'
import { t } from '@/modules/translations'
import { debounce } from '@/utils/debounce'

void emojiStore.loadData()

type Props = {
	textareaRef?: HTMLTextAreaElement | null
	resizable?: boolean
	onKeyEnter: () => void
}

let { textareaRef = $bindable(null), resizable = true, onKeyEnter }: Props = $props()

const isPopupContext = $derived(!$isMessengerFrameOpen)

// Inline emoji picker wrapper ref and state
let emojiPicker: EmojiPickerProps | undefined = $state()
let isEmojiPickerOpen = $state(false)
let emojiPickerListboxId = $state('')
let emojiPickerActiveDescendantId: string | undefined = $state(undefined)

$effect(() => {
	if (isEmojiPickerOpen) {
		return registerEscapeHandler(() => emojiPicker?.close())
	}
})

const handleEmojiInsert = (newText: string, newCursorPosition: number) => {
	setMessengerInputValue(newText)

	requestAnimationFrame(() => {
		if (textareaRef) {
			textareaRef.value = newText
			textareaRef.setSelectionRange(newCursorPosition, newCursorPosition)
			textareaRef.focus()
			updateTextareaValueDataset(newText)
			storeTextareaHeight()
		}
	})
}

const getTextareaParentElement = (): HTMLDivElement | null => {
	if (!textareaRef) return null
	return textareaRef.parentNode as HTMLDivElement | null
}

const updateTextareaValueDataset = (value: string) => {
	const parent = getTextareaParentElement()
	if (!parent) return
	parent.dataset.textareaValue = value
}

// Store textarea height to change scroll position of list when textarea height changes
const storeTextareaHeight = () => {
	const parent = getTextareaParentElement()
	if (!parent) return
	textareaHeight.set(parent.clientHeight)
}

const handleInput = (event: Event) => {
	const target = event.target as HTMLTextAreaElement

	// Disallows typing longer texts than 'maxlength' on Android
	target.value = target.value.substring(0, TEXTAREA_MAX_LENGTH)

	setMessengerInputValue(target.value)
	updateTextareaValueDataset(target.value)
	storeTextareaHeight()
	refreshTyping()

	// Update emoji suggestions
	emojiPicker?.handleInputChange()

	if (target.value.length === TEXTAREA_MAX_LENGTH) {
		warningMessage($t('warningBar.maxLength'))
	}
}

const handlePaste = (event: ClipboardEvent) => {
	const files = event.clipboardData?.files
	if (!files || files.length === 0 || isPopupContext) return // Disallow pasting files in Popup
	event.preventDefault()
	checkAndAddFiles([...files])
}

const stopTyping = debounce(() => {
	stopVisitorTyping()
}, 3000)

const refreshTyping = () => {
	startVisitorTyping()
	stopTyping()
}

const handleKeyDown = (event: KeyboardEvent) => {
	// Let emoji picker handle navigation first
	if (emojiPicker?.handleKeyDown(event)) {
		event.preventDefault()
		return
	}

	if (event.key === Key.Enter) {
		// Don't use enter key to send message on touch devices
		if (!event.shiftKey && !isTouchDevice()) {
			event.preventDefault()
			onKeyEnter()
		}
	}
}

// Scrolls the textarea into view when focused
// This is useful for mobile devices where the keyboard can cover the textarea
const handleFocus = () => {
	if (isTouchDevice()) {
		setTimeout(() => textareaRef?.scrollIntoView({ behavior: 'instant' }), 500)
	}
}

// Handle cursor position changes (click, arrow keys without picker)
const handleSelectionChange = () => {
	emojiPicker?.handleSelectionChange()
}

$effect(() => {
	textareaRef && updateTextareaValueDataset($messengerInputValue)
})
</script>

<div class="relative flex-1">
	{#if !isPopupContext}
		<InlineEmojiPicker
			bind:this={emojiPicker}
			{textareaRef}
			inputValue={$messengerInputValue}
			bind:isOpen={isEmojiPickerOpen}
			bind:listboxId={emojiPickerListboxId}
			bind:activeDescendantId={emojiPickerActiveDescendantId}
			onEmojiInsert={handleEmojiInsert}
		/>
	{/if}
	<div
		class={[
			'textarea-container text-base-input',
			$isSmallInputFontEnabled && 'text-sm',
			resizable && 'textarea-container-resizable',
		]}
	>
		<textarea
			bind:this={textareaRef}
			class={['bg-transparent resize-none overflow-hidden focus:outline-none', !resizable && 'truncate whitespace-pre']}
			placeholder={$t('chatInput.placeholder')}
			value={$messengerInputValue}
			rows="1"
			maxlength={TEXTAREA_MAX_LENGTH}
			onfocus={handleFocus}
			oninput={handleInput}
			onkeydown={handleKeyDown}
			onkeyup={handleSelectionChange}
			onclick={handleSelectionChange}
			onpaste={handlePaste}
			data-testid={testIds.textarea}
			role={isEmojiPickerOpen ? 'combobox' : undefined}
			aria-autocomplete={isEmojiPickerOpen ? 'list' : undefined}
			aria-expanded={isEmojiPickerOpen}
			aria-controls={isEmojiPickerOpen ? emojiPickerListboxId : undefined}
			aria-activedescendant={emojiPickerActiveDescendantId}
			aria-haspopup={isEmojiPickerOpen ? 'listbox' : undefined}
		></textarea>
	</div>
</div>

<style lang="postcss">
.textarea-container {
	@apply grid flex-1 my-3 overflow-x-hidden overflow-y-auto overscroll-contain max-h-[32px];
	scrollbar-width: none;
}

.textarea-container::-webkit-scrollbar {
	display: none;
}

.textarea-container-resizable {
	@apply max-h-[125px];
}

.textarea-container::after {
	/* Space needed to prevent jumpy behavior */
	content: attr(data-textarea-value) ' ';
	white-space: pre-wrap;
	visibility: hidden;
	overflow-wrap: break-word;
	word-break: break-word;
}

.textarea-container::after,
textarea {
	@apply ml-4 mr-3;
	grid-area: 1 / 1 / 2 / 2;
	font-size: inherit;
}

textarea::placeholder {
	@apply overflow-hidden break-all;
}
</style>
